import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Package, RefreshCw, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Grain } from "../components/Grain";
import { SpiceNote } from "../components/SpiceNote";

const statusStyle = {
    paid: { bg: "bg-[#2f5d1a]", text: "PAID", teText: "చెల్లించబడింది" },
    pending: { bg: "bg-[color:var(--kp-ochre-warm)]", text: "PENDING", teText: "పెండింగ్" },
    pending_placeholder: { bg: "bg-[color:var(--kp-ochre-warm)]", text: "PENDING", teText: "పెండింగ్" },
    whatsapp_sent: { bg: "bg-[color:var(--kp-red)]", text: "WhatsApp", teText: "వాట్సాప్" },
    signature_failed: { bg: "bg-black", text: "FAILED", teText: "విఫలం" },
};

const OrdersPage = () => {
    const { user, loading: authLoading, requireAuth } = useAuth();
    const { mergeItems, setOpen: setCartOpen } = useCart();
    const { lang } = useLang();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reorderedId, setReorderedId] = useState("");

    const t = (en, te) => (lang === "en" ? en : te);

    const handleReorder = (o) => {
        const items = (o.items || []).map((it) => ({
            id: it.id,
            name: { en: it.name_en, te: it.name_te },
            image: `/img/products/${it.id}.jpg`,
            weight: it.weight,
            price: it.price,
            qty: it.qty,
        }));
        if (items.length === 0) return;
        mergeItems(items);
        setReorderedId(o._id);
        setCartOpen(true);
        setTimeout(() => setReorderedId(""), 2000);
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            requireAuth(() => window.location.reload(), "login");
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/orders/my");
                setOrders(data);
            } catch {
                setOrders([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [user, authLoading, requireAuth]);

    return (
        <div className="min-h-screen bg-[color:var(--kp-cream)]">
            <Grain />
            <Header />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-[1000px] mx-auto px-6 md:px-10">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 hover:text-[color:var(--kp-red)] mb-6"
                        data-testid="back-home"
                    >
                        <ArrowLeft size={12} />
                        {t("Back to the shop", "షాప్‌కు తిరిగి")}
                    </Link>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                        className={`text-6xl md:text-7xl leading-[0.95] tracking-editorial mb-4 ${
                            lang === "en" ? "font-serif-display" : "font-telugu-display"
                        }`}
                        data-testid="orders-title"
                    >
                        {t("Your", "మీ")}{" "}
                        <em className="italic text-[color:var(--kp-red)]">
                            {t("basket, again.", "ఆర్డర్‌లు.")}
                        </em>
                    </motion.h1>
                    <p className="text-[color:var(--kp-ink)]/70 max-w-lg mb-10">
                        {t(
                            "Every pouch you've ordered from the family kitchen. Repeat any order in one tap.",
                            "మీరు ఇంతకు ముందు తీసుకున్న అన్ని ఆర్డర్‌లు. ఒక్క క్లిక్‌తో మళ్ళీ ఆర్డర్ చేయండి.",
                        )}
                    </p>

                    <SpiceNote testId="orders-spice-custom-note" className="mb-8 max-w-lg" />

                    {loading ? (
                        <div className="text-[color:var(--kp-ink)]/50 py-16" data-testid="orders-loading">
                            {t("Loading…", "లోడ్ అవుతోంది…")}
                        </div>
                    ) : !user ? (
                        <div className="border border-[color:var(--kp-ink)]/15 p-8 text-center" data-testid="orders-need-auth">
                            {t("Please log in to view your orders.", "మీ ఆర్డర్‌లు చూడటానికి లాగిన్ అవండి.")}
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="border border-dashed border-[color:var(--kp-ink)]/20 p-12 text-center" data-testid="orders-empty">
                            <Package className="mx-auto mb-3 opacity-40" size={28} />
                            <p className="font-serif-display italic text-2xl text-[color:var(--kp-ink)]/60">
                                {t("No orders yet.", "ఖాళీ ఆర్డర్‌లు.")}
                            </p>
                            <p className="text-sm text-[color:var(--kp-ink)]/60 mt-2">
                                {t("Your first order will live here.", "మీ మొదటి ఆర్డర్ ఇక్కడ కనిపిస్తుంది.")}
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-4" data-testid="orders-list">
                            {orders.map((o) => {
                                const badge = statusStyle[o.status] || statusStyle.pending;
                                return (
                                    <li
                                        key={o._id}
                                        className="border border-[color:var(--kp-ink)]/15 bg-[color:var(--kp-cream)] p-5 md:p-6"
                                        data-testid={`order-${o._id}`}
                                    >
                                        <div className="flex justify-between items-start gap-4 mb-3">
                                            <div>
                                                <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                                                    №{o._id.slice(-8).toUpperCase()} ·{" "}
                                                    {new Date(o.created_at).toLocaleDateString("en-IN", {
                                                        day: "numeric",
                                                        month: "short",
                                                        year: "numeric",
                                                    })}
                                                </div>
                                                <div className="font-serif-display text-2xl mt-1">
                                                    ₹{o.total}{" "}
                                                    <span className="text-sm text-[color:var(--kp-ink)]/60 font-body-sans">
                                                        · {o.items.length} items
                                                    </span>
                                                </div>
                                            </div>
                                            <span
                                                className={`${badge.bg} text-[color:var(--kp-cream)] text-[10px] tracking-mono-wide px-3 py-1`}
                                            >
                                                {lang === "en" ? badge.text : badge.teText}
                                            </span>
                                        </div>
                                        <ul className="text-sm text-[color:var(--kp-ink)]/75 space-y-0.5">
                                            {o.items.map((it, i) => (
                                                <li key={i}>
                                                    {it.qty} × {lang === "en" ? it.name_en : it.name_te}{" "}
                                                    <span className="text-[color:var(--kp-ink)]/50">
                                                        · {it.weight}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="mt-3 pt-3 border-t border-[color:var(--kp-ink)]/10 flex items-center gap-3 flex-wrap">
                                            <div className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 flex flex-wrap gap-x-4 gap-y-1 flex-1 min-w-0">
                                                <span>{o.payment_method?.toUpperCase()}</span>
                                                {o.razorpay_payment_id && (
                                                    <span>PAY: {o.razorpay_payment_id.slice(-10)}</span>
                                                )}
                                                <span>
                                                    Ship to {o.address?.city}, {o.address?.state}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => handleReorder(o)}
                                                disabled={!o.items || o.items.length === 0}
                                                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] tracking-mono-wide font-bold transition-all ${
                                                    reorderedId === o._id
                                                        ? "bg-[color:var(--kp-banana)] text-[color:var(--kp-cream)]"
                                                        : "bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] hover:bg-black"
                                                } disabled:opacity-40`}
                                                data-testid={`reorder-${o._id}`}
                                                aria-label={t("Reorder this order", "మళ్ళీ ఆర్డర్ చేయండి")}
                                            >
                                                {reorderedId === o._id ? (
                                                    <>
                                                        <Check size={12} />
                                                        {t("ADDED", "చేర్చబడింది")}
                                                    </>
                                                ) : (
                                                    <>
                                                        <RefreshCw size={12} />
                                                        {t("REORDER", "మళ్ళీ")}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default OrdersPage;
